"use client"

import type {
  publicKeyVerifierMachine,
  SendNearTransaction,
} from "@src/components/DefuseSDK/features/machines/publicKeyVerifierMachine"
import { WalletBannedDialog } from "@src/components/WalletBannedDialog"
import { WalletVerificationDialog } from "@src/components/WalletVerificationDialog"
import type { walletVerificationMachine } from "@src/features/auth/machines/walletVerificationMachine"
import { ChainType, useConnectWallet } from "@src/hooks/useConnectWallet"
import { useAuthActor } from "@src/providers/AppBootstrapProvider"
import { useMixpanel } from "@src/providers/MixpanelProvider"
import { useNearWallet } from "@src/providers/NearWalletProvider"
import { useBypassedWalletsStore } from "@src/stores/useBypassedWalletsStore"
import { hashForAnalytics } from "@src/utils/analyticsSanitize"
import { isAnalyticsSuppressed } from "@src/utils/analyticsSuppression"
import { trackEvent } from "@src/utils/trackEvent"
import { useSelector } from "@xstate/react"
import { useEffect, useState } from "react"
import type { ActorRefFrom } from "xstate"

export function WalletVerificationProvider() {
  const { state, signOut } = useConnectWallet()
  const mixPanel = useMixpanel()
  const authRef = useAuthActor()
  const nearWallet = useNearWallet()
  const addBypassedWalletAddress = useBypassedWalletsStore(
    (s) => s.addBypassedWalletAddress
  )

  // authMachine state — drives both the banned/unsafe dialog and the
  // verification + pubkey dialogs below. Safety + ban gating now runs entirely
  // inside authMachine (see authMachine.gating); this component only reads.
  const isSafetyBlocked = useSelector(authRef, (snap) =>
    snap.matches({ gating: "safety_blocked" })
  )
  const isSafetyUnreachable = useSelector(authRef, (snap) =>
    snap.matches({ gating: "safety_unreachable" })
  )
  const isVerifying = useSelector(
    authRef,
    (snap) =>
      snap.matches({ authenticating: "wallet_verifying" }) ||
      snap.matches({ authenticating: "reauthenticating" })
  )
  const isPubkeyVerifying = useSelector(authRef, (snap) =>
    snap.matches({ authenticating: "pubkey_verifying" })
  )
  const isReauthenticating = useSelector(authRef, (snap) =>
    snap.matches({ authenticating: "reauthenticating" })
  )
  const authAddress = useSelector(authRef, (snap) => snap.context.address)
  const nearAccountId = useSelector(
    authRef,
    (snap) => snap.context.nearAccount?.accountId ?? null
  )

  useEffect(() => {
    const sub = authRef.on("WALLET_DISCONNECT_REQUESTED", (event) => {
      void signOut({ id: event.chainType as ChainType })
    })
    return () => sub.unsubscribe()
  }, [authRef, signOut])

  const [walletVerInfo, setWalletVerInfo] = useState({
    isVerifying: false,
    hadError: false,
  })
  useEffect(() => {
    if (!isVerifying) {
      setWalletVerInfo({ isVerifying: false, hadError: false })
      return
    }

    const walletVerRef = authRef.getSnapshot().children.walletVerRef as
      | ActorRefFrom<typeof walletVerificationMachine>
      | undefined
    if (!walletVerRef) return

    let hadError = walletVerRef.getSnapshot().context.hadError
    const sub = walletVerRef.subscribe((snap) => {
      if (!hadError && snap.context.hadError) {
        trackEvent("wallet_verification_failed")
      }
      hadError = snap.context.hadError
      setWalletVerInfo({
        isVerifying: snap.matches("verifying"),
        hadError: snap.context.hadError,
      })
    })

    return () => sub.unsubscribe()
  }, [isVerifying, authRef])

  const [pubkeyVerInfo, setPubkeyVerInfo] = useState({
    isChecked: false,
    isAdding: false,
  })

  useEffect(() => {
    if (!isPubkeyVerifying) {
      setPubkeyVerInfo({ isChecked: false, isAdding: false })
      return
    }

    const pubkeyVerRef = authRef.getSnapshot().children.pubkeyVerRef as
      | ActorRefFrom<typeof publicKeyVerifierMachine>
      | undefined
    if (!pubkeyVerRef) return

    const current = pubkeyVerRef.getSnapshot()
    setPubkeyVerInfo({
      isChecked: current.matches("checked"),
      isAdding: current.matches("adding"),
    })

    const sub = pubkeyVerRef.subscribe((snap) => {
      setPubkeyVerInfo({
        isChecked: snap.matches("checked"),
        isAdding: snap.matches("adding"),
      })
    })

    return () => sub.unsubscribe()
  }, [isPubkeyVerifying, authRef])

  if (isSafetyBlocked && authAddress != null) {
    return (
      <WalletBannedDialog
        open={true}
        onAbort={() => {
          authRef.send({ type: "ABORT_GATING" })
          if (state.chainType != null) {
            void signOut({ id: state.chainType })
          }
        }}
        onBypass={() => {
          addBypassedWalletAddress(authAddress)
          authRef.send({ type: "BYPASS_SAFETY" })
          if (isAnalyticsSuppressed()) return
          void hashForAnalytics(authAddress)
            .then((hashedAddress) => {
              const payload = {
                wallet_hash: hashedAddress,
                wallet_type: state.chainType,
              }
              mixPanel?.track("wallet_bypassed", payload)
              window.gtag?.("event", "wallet_bypassed", payload)
            })
            .catch(() => {})
        }}
      />
    )
  }

  if (isSafetyUnreachable) {
    return (
      <WalletBannedDialog
        open={true}
        variant="unreachable"
        onRetry={() => authRef.send({ type: "RETRY_SAFETY" })}
        onAbort={() => {
          authRef.send({ type: "ABORT_GATING" })
          if (state.chainType != null) {
            void signOut({ id: state.chainType })
          }
        }}
        onBypass={() => {
          authRef.send({ type: "PROCEED_UNVERIFIED" })
        }}
      />
    )
  }

  if (
    isPubkeyVerifying &&
    (pubkeyVerInfo.isChecked || pubkeyVerInfo.isAdding)
  ) {
    const sendNearTransaction: SendNearTransaction = async (tx) => {
      await nearWallet.signAndSendTransactions({ transactions: [tx] })
      // Caller only checks for non-null; the hash is never read.
      return { txHash: "done" }
    }

    return (
      <WalletVerificationDialog
        open={true}
        onConfirm={() => {
          const ref = authRef.getSnapshot().children.pubkeyVerRef as
            | ActorRefFrom<typeof publicKeyVerifierMachine>
            | undefined
          ref?.send({ type: "ADD_PUBLIC_KEY", sendNearTransaction })
        }}
        onCancel={() => {
          const ref = authRef.getSnapshot().children.pubkeyVerRef as
            | ActorRefFrom<typeof publicKeyVerifierMachine>
            | undefined
          ref?.send({ type: "ABORT_ADD_PUBLIC_KEY" })
          if (state.chainType != null) {
            void signOut({ id: state.chainType })
          }
        }}
        isVerifying={false}
        isFailure={false}
        isConfirmingPubKey={pubkeyVerInfo.isChecked}
        isAddingPubKey={pubkeyVerInfo.isAdding}
        accountId={nearAccountId}
      />
    )
  }

  // Wallet verification dialog — auth starts when bootstrap enters verifying.active.
  if (isVerifying) {
    return (
      <WalletVerificationDialog
        open={true}
        onConfirm={() => {
          const ref = authRef.getSnapshot().children.walletVerRef as
            | ActorRefFrom<typeof walletVerificationMachine>
            | undefined
          if (ref == null) return
          trackEvent(
            walletVerInfo.hadError
              ? "wallet_verification_retry_requested"
              : "wallet_verification_requested"
          )
          ref.send({ type: "START" })
        }}
        onCancel={() => {
          authRef.send({ type: "ABORT_VERIFICATION" })
          if (state.chainType != null) {
            void signOut({ id: state.chainType })
          }
        }}
        isVerifying={walletVerInfo.isVerifying || isReauthenticating}
        isFailure={walletVerInfo.hadError}
        isSessionExpired={state.isSessionExpired}
        isPasskey={state.chainType === ChainType.WebAuthn}
      />
    )
  }

  return null
}
